import React, {
  RefObject,
  SyntheticEvent,
  useCallback,
} from 'react';
import clsx from 'clsx';
import Autocomplete, {
  AutocompleteClasses,
  AutocompleteProps,
} from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import { InputProps as StandardInputProps } from '@mui/material/Input/Input';
import SearchIcon from '@mui/icons-material/SearchRounded';

export type Props<T> = Pick<
    AutocompleteProps<T, boolean, boolean, boolean>,
    'className'
    | 'value'
    | 'defaultValue'
    | 'options'
    | 'renderOption'
    | 'renderTags'
    | 'ListboxComponent'
    | 'ListboxProps'
    | 'disabled'
    | 'multiple'
    | 'autoComplete'
    | 'loading'
    | 'loadingText'
    | 'openOnFocus'
    | 'includeInputInList'
    | 'filterSelectedOptions'
    | 'noOptionsText'
    | 'blurOnSelect'
    | 'popupIcon'
    | 'freeSolo'
    | 'open'
    | 'clearOnBlur'
    | 'disablePortal'
    | 'onBlur'
    | 'onFocus'
    | 'onOpen'
    | 'onClose'
    | 'onInputChange'
    | 'filterOptions'
    | 'isOptionEqualToValue'
    | 'selectOnFocus'
    | 'classes'
    | 'inputValue'
    | 'groupBy'
    | 'renderGroup'
    | 'clearIcon'
  >
  & {
  id?: string;
  label?: string;
  required?: boolean;
  getLabel: (option: T) => string;
  inputRef?: RefObject<any>;
  readonly?: boolean;
  dropdownClassName?: string;
  InputProps?: Partial<StandardInputProps>;
  onChange: (value: T | T[]) => void;
  disableRemove?: boolean;
  getOptionDisabled?: (option: T) => boolean;
  muiClasses?: Partial<AutocompleteClasses>;
  placeholder?: string;
};

export default function AutoCompletedInput<T>({
  id,
  className,
  placeholder,
  value,
  options = [],
  getLabel,
  renderOption,
  onChange,
  multiple,
  disabled,
  filterSelectedOptions = true,
  autoComplete = false,
  includeInputInList = false,
  loading = false,
  clearOnBlur = true,
  openOnFocus = false,
  onOpen,
  onClose,
  onInputChange,
  filterOptions,
  loadingText = 'Loading...',
  noOptionsText = 'No Options',
  isOptionEqualToValue,
  blurOnSelect,
  inputRef,
  popupIcon,
  muiClasses,
  readonly,
  renderTags,
  open,
  freeSolo,
  dropdownClassName,
  ListboxComponent,
  disablePortal,
  onBlur,
  onFocus,
  defaultValue,
  InputProps = {},
}: Props<T>) {
  const autoCompleteClasses = clsx('populate-autocomplete', className, { disabled });
  const handleChange = (event: SyntheticEvent, value: any) => onChange(value);

  const _renderTags = renderTags ?? useCallback((value: T[]) => (
    value.map((v: T) => (
      <span
        key={getLabel(v)}
        className="mr-1"
      >
        {getLabel(v)}
      </span>
    ))
  ), [getLabel]);

  const icon = popupIcon ?? <SearchIcon className="search-icon" />;

  return (
    <Autocomplete
      clearIcon={null}
      blurOnSelect={blurOnSelect}
      className={autoCompleteClasses}
      onChange={handleChange}
      value={value}
      multiple={multiple}
      defaultValue={defaultValue}
      id={id}
      onBlur={onBlur}
      onFocus={onFocus}
      options={options}
      getOptionLabel={(option) => getLabel(option as T)}
      openOnFocus={openOnFocus}
      filterSelectedOptions={filterSelectedOptions}
      filterOptions={filterOptions}
      loading={loading}
      autoComplete={autoComplete}
      includeInputInList={includeInputInList}
      onOpen={onOpen}
      onClose={onClose}
      onInputChange={onInputChange}
      disabled={disabled}
      popupIcon={!readonly && icon}
      renderTags={_renderTags}
      loadingText={loadingText}
      ListboxComponent={ListboxComponent}
      noOptionsText={noOptionsText}
      isOptionEqualToValue={isOptionEqualToValue}
      classes={muiClasses}
      readOnly={readonly}
      renderOption={renderOption}
      freeSolo={freeSolo}
      disablePortal={disablePortal}
      slotProps={{
        paper: {
          className: dropdownClassName ?? '',
        },
      }}
      open={open}
      clearOnBlur={clearOnBlur}
      renderInput={(params) => (
        <TextField
          {...params}
          id={id}
          value={value}
          inputRef={inputRef}
          placeholder={placeholder}
          InputProps={{
            ...params.InputProps,
            ...InputProps,
          }}
        />
      )}
    />
  );
}
